import { Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import SectionEyebrow from '../components/SectionEyebrow';
import ImageSlot from '../components/ImageSlot';
import Reveal from '../components/Reveal';

export default function Land() {
  return (
    <div>
      {/* HERO: full-bleed cinematic */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <ImageSlot
            id="land.hero"
            className="absolute inset-0 w-full h-full"
            aspect="auto"
            loading="eager"
            position="center"
          />
        </div>
        {/* Tonal scrim */}
        <div className="absolute inset-0 bg-gradient-to-b from-forest-950/30 via-forest-950/10 to-forest-950/85" />
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="relative z-10 w-full pb-20 lg:pb-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-cream">
            <Breadcrumb current="The Land" tone="dark" />
            <div className="mt-10 grid lg:grid-cols-12 gap-12 items-end">
              <div className="lg:col-span-8">
                <SectionEyebrow>
                  <span className="text-sand-300">
                    160 Hectares &middot; San Clemente
                  </span>
                </SectionEyebrow>
                <h1 className="mt-6 font-display text-cream leading-[0.95] tracking-tight text-[clamp(2.75rem,8vw,7.5rem)]">
                  Where the mountains meet the{' '}
                  <span className="italic text-sand-300">Caribbean.</span>
                </h1>
              </div>
              <div className="lg:col-span-4">
                <p className="text-cream/85 text-lg leading-relaxed">
                  San Clemente, Valle de la Estrella, Limon Province. Between
                  Limon city and Cahuita National Park.
                </p>
                <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-widest-2 text-cream/60">
                  <span className="w-10 h-px bg-cream/40" />
                  9.8&deg; N &middot; 82.9&deg; W
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RARE ASSET */}
      <section className="py-24 lg:py-32 bg-cream relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <Reveal as="div" className="lg:col-span-5">
              <SectionEyebrow>A rare asset, a rare moment</SectionEyebrow>
              <p className="mt-8 font-display text-3xl lg:text-5xl text-forest-900 leading-snug">
                Costa Rica&rsquo;s Caribbean coast has been the country&rsquo;s{' '}
                <span className="italic text-clay-500">quiet half</span> for
                decades. That changes now.
              </p>
            </Reveal>
            <Reveal as="div" delay={100} className="lg:col-span-7 lg:pt-6">
              <p className="text-forest-900/80 text-lg leading-relaxed">
                Limon Province was just named the #1 global destination of
                2026. Route 36 has been rebuilt from the airport to Panama.
                International buyers are arriving, and the land available at
                this scale, with this access, with clean title, is essentially
                nonexistent. Nativa is what happens when the right 160
                hectares becomes available at exactly the right inflection
                point.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-6">
                <Pull big="#1" label="Global destination 2026" sub="Skyscanner" />
                <Pull big="2 to 5x" label="Pacific land premium" sub="Today" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THREE THINGS: image-led cards */}
      <section className="py-24 lg:py-32 bg-forest-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <SectionEyebrow>The geography</SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
              Three things that{' '}
              <span className="italic text-clay-500">
                do not come together
              </span>{' '}
              twice.
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Reveal>
              <Geography
                imageId="land.geo.hectares"
                big="160"
                label="Contiguous hectares"
                body="Multiple folios consolidated under clean title. One property, one vision, one set of restrictions. Comparable contiguous parcels on either coast trade rarely and quietly, almost never with this frontage."
              />
            </Reveal>
            <Reveal delay={100}>
              <Geography
                imageId="land.geo.river"
                big="River"
                label="From mountain to sea"
                body="A working river runs the length of the property: from forested highlands to ocean. Fifty to one hundred meters of protected forest corridor on each bank, in perpetuity. The river is the spine of the masterplan."
              />
            </Reveal>
            <Reveal delay={200}>
              <Geography
                imageId="land.geo.beach"
                big="< 1km"
                label="To the beach concession"
                body="A walkable beach concession at the river mouth. Titled rights for community use. The beach club site is already chosen. Walk, bike, or shuttle from any neighborhood in under fifteen minutes."
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* FULL-BLEED PHOTO BREAK */}
      <section className="relative h-[60vh] lg:h-[80vh] overflow-hidden">
        <ImageSlot
          id="land.geo.river"
          className="absolute inset-0 w-full h-full"
          aspect="auto"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-950/70 via-forest-950/20 to-transparent" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full">
            <div className="max-w-2xl text-cream">
              <SectionEyebrow>
                <span className="text-sand-300">The river</span>
              </SectionEyebrow>
              <p className="mt-6 font-display text-4xl lg:text-6xl italic leading-tight">
                &ldquo;The river decides. The forest stays. Architecture
                defers.&rdquo;
              </p>
              <div className="mt-6 eyebrow text-cream/70">
                The Nativa design covenant
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GETTING THERE */}
      <section className="py-24 lg:py-32 bg-forest-900 text-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-7">
              <SectionEyebrow>
                <span className="text-sand-300">Getting there</span>
              </SectionEyebrow>
              <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight">
                Twenty minutes from a runway.{' '}
                <span className="italic text-sand-300">
                  Two-and-a-half hours
                </span>{' '}
                from a capital.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-6">
              <p className="text-cream/80 text-lg leading-relaxed">
                Caribbean Costa Rica used to be hard to reach. The new Route
                36 corridor changed that. Nativa sits at the center of it.
              </p>
            </div>
          </div>

          {/* Photo + stylized map paired */}
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <ImageSlot
                id="land.infrastructure"
                className="w-full"
                aspect="16 / 11"
              />
              <div className="mt-3 text-xs uppercase tracking-widest-2 text-cream/50">
                Route 36 &middot; freshly paved &middot; airport to Panama
              </div>
            </div>
            <div className="lg:col-span-5 bg-forest-950/60 border border-cream/10 p-6 lg:p-8">
              <svg
                viewBox="0 0 800 460"
                className="w-full h-auto"
                aria-label="Map of Nativa and surrounding area"
              >
                <rect width="800" height="460" fill="#1f2f24" />
                <path
                  d="M0 280 Q200 260 400 290 T800 280 L800 460 L0 460 Z"
                  fill="#0f1d18"
                />
                <path
                  d="M0 210 Q150 110 300 190 T600 170 T800 210 L800 280 L0 280 Z"
                  fill="#26392b"
                  opacity="0.85"
                />
                <path
                  d="M50 360 Q300 320 500 330 T780 290"
                  stroke="#d9bc88"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="6 6"
                />
                <text
                  x="280"
                  y="310"
                  fill="#d9bc88"
                  fontSize="10"
                  letterSpacing="3"
                  fontFamily="Inter"
                >
                  ROUTE 36
                </text>

                <Marker x={120} y={350} label="San Jose" sub="2.5 HRS WEST" />
                <Marker x={300} y={330} label="Limon Airport" sub="20 MIN" />
                <Marker x={340} y={350} label="Limon City" />
                <Marker x={460} y={340} label="Sloth Sanctuary" sub="10 MIN" />
                <BigMarker x={560} y={350} label="NATIVA" sub="San Clemente" />
                <Marker
                  x={650}
                  y={365}
                  label="Cahuita NP"
                  sub="CORAL REEF . 20 MIN"
                />
                <Marker
                  x={730}
                  y={380}
                  label="Puerto Viejo"
                  sub="SURF . 35 MIN"
                />

                <g transform="translate(50, 60)">
                  <circle
                    r="20"
                    fill="none"
                    stroke="#d9bc88"
                    strokeWidth="1"
                    opacity="0.6"
                  />
                  <path d="M0 -20 L4 0 L0 20 L-4 0 Z" fill="#d9bc88" />
                  <text
                    x="0"
                    y="-26"
                    fill="#d9bc88"
                    fontSize="10"
                    textAnchor="middle"
                  >
                    N
                  </text>
                </g>

                <text
                  x="400"
                  y="50"
                  fill="#90b193"
                  fontSize="11"
                  letterSpacing="4"
                  textAnchor="middle"
                >
                  CARIBBEAN SEA
                </text>
                <text
                  x="400"
                  y="190"
                  fill="#658d6b"
                  fontSize="11"
                  letterSpacing="4"
                  textAnchor="middle"
                >
                  TALAMANCA RANGE
                </text>
              </svg>
              <div className="mt-4 text-xs uppercase tracking-widest-2 text-cream/50">
                ~ 30 KM &middot; NATIVA &middot; San Clemente
              </div>
            </div>
          </div>

          {/* Travel times list */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-5 gap-6">
            <TravelTime time="20 min" place="Limon Intl. Airport" />
            <TravelTime time="2.5 hrs" place="San Jose Intl." />
            <TravelTime time="10 min" place="Sloth Sanctuary" />
            <TravelTime time="20 min" place="Cahuita National Park" />
            <TravelTime time="35 min" place="Puerto Viejo (surf . music)" />
          </div>
        </div>
      </section>

      {/* NEIGHBORHOOD: photo-led tall cards */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <SectionEyebrow>The neighborhood</SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
              You do not just buy land.{' '}
              <span className="italic text-clay-500">
                You inherit a place.
              </span>
            </h2>
            <p className="mt-6 text-forest-900/80 text-lg leading-relaxed">
              Caribbean Costa Rica is not a manufactured destination. It is a
              living place, with a culture, a cuisine, and an ecosystem worth
              showing up for.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <Reveal>
              <PlaceCard
                imageId="land.wildlife"
                eyebrow="The wildlife"
                title="A wildlife corridor, not a backyard."
                body="Scarlet macaws, three-toed sloths, white-faced capuchins, agoutis, kinkajous, blue-jeans frogs. Cahuita's reef and the rainforest meet on this stretch."
                meta="Cahuita National Park . 20 min"
              />
            </Reveal>
            <Reveal delay={100}>
              <PlaceCard
                imageId="land.culture"
                eyebrow="The culture"
                title="Rice, beans, and coconut."
                body="Afro-Caribbean cuisine is its own world here: fish in coconut sauce, jerk-spiced chicken, rondon, plantain everything. The Bribri indigenous community shares cacao traditions stretching back centuries."
                meta="Living cultures . Not theme parks"
              />
            </Reveal>
            <Reveal delay={200}>
              <PlaceCard
                imageId="land.forest"
                eyebrow="The forest"
                title="Bamboo, ceiba, and balsa."
                body="The Talamanca range drops directly into the sea here, and the rainforest is among the most biodiverse on earth. The land's existing canopy stays. Architecture defers to it."
                meta="Native canopy . Preserved"
              />
            </Reveal>
          </div>

          {/* Secondary wildlife strip */}
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <div className="aspect-[3/4] overflow-hidden">
              <ImageSlot id="land.toucan" className="w-full h-full" aspect="auto" />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <ImageSlot id="land.heliconia" className="w-full h-full" aspect="auto" />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <ImageSlot id="land.sea-cliff" className="w-full h-full" aspect="auto" />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <ImageSlot id="land.river-bamboo" className="w-full h-full" aspect="auto" />
            </div>
          </div>
        </div>
      </section>

      {/* INFRASTRUCTURE */}
      <section className="py-24 lg:py-32 bg-forest-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <SectionEyebrow>The reason it works</SectionEyebrow>
              <h2 className="mt-6 font-display text-5xl lg:text-6xl leading-tight text-forest-900">
                Caribbean access,{' '}
                <span className="italic text-clay-500">finally</span> sorted.
              </h2>
              <p className="mt-6 text-forest-900/80 text-lg leading-relaxed">
                Costa Rica&rsquo;s Caribbean coast historically lagged the
                Pacific because access was hard. Now it is not. The Route 36
                corridor was repaved, bridges rebuilt, and the Limon airport
                is being expanded. The infrastructure caught up to the
                geography.
              </p>
              <p className="mt-6 text-forest-900/80 leading-relaxed">
                That is why prices are climbing, and why the gap between
                Caribbean and Pacific land will not stay this wide for long.
              </p>

              <div className="mt-10 aspect-[16/10] overflow-hidden">
                <ImageSlot id="land.geo.hectares" className="w-full h-full" aspect="auto" />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-px bg-forest-900/10">
              <Infra
                title="Roads"
                body="Paved Route 36 to the property entrance. All bridges recently rebuilt or replaced."
              />
              <Infra
                title="Air"
                body="Limon International Airport. Expansion in progress for international flights."
              />
              <Infra
                title="Water"
                body="Mountain-fed water rights secured. On-site treatment infrastructure planned."
              />
              <Infra
                title="Power"
                body="Grid-connected with solar augmentation across community amenities."
              />
              <Infra
                title="Connectivity"
                body="Fiber to the property line. Starlink-ready throughout for redundancy."
              />
              <Infra
                title="Title"
                body="Multiple folios consolidated. Beach concession titled. Clean chain of ownership."
              />
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <ImageSlot
            id="land.geo.beach"
            className="absolute inset-0 w-full h-full"
            aspect="auto"
            position="center"
          />
        </div>
        <div className="absolute inset-0 bg-forest-950/70" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center text-cream">
          <div className="font-display text-sand-300 text-7xl leading-none">
            &ldquo;
          </div>
          <p className="mt-4 font-display text-4xl lg:text-5xl leading-tight italic">
            The land tells you what it{' '}
            <span className="text-sand-300">wants to be.</span> Our job is to
            listen.
          </p>
          <div className="mt-8 eyebrow text-cream/70">
            The Nativa Design Principle
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-forest-900 text-cream">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <SectionEyebrow className="justify-center">
            <span className="text-sand-300">What comes next</span>
          </SectionEyebrow>
          <h2 className="mt-6 font-display text-4xl lg:text-6xl leading-tight">
            The land is the foundation.{' '}
            <span className="italic text-sand-300">
              The vision is what gets built on it.
            </span>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-cream/80 leading-relaxed">
            See how 160 hectares becomes a regenerative community. Eight
            neighborhoods, four lot tiers, and a single river spine.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/vision"
              className="px-7 py-4 bg-sand-300 text-forest-900 font-medium tracking-wide hover:bg-sand-200 transition-colors"
            >
              See the Vision
            </Link>
            <Link
              to="/lots"
              className="px-7 py-4 border border-cream/30 text-cream font-medium tracking-wide hover:bg-cream/10 transition-colors"
            >
              Explore the Lots
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* Subcomponents */

function Pull({
  big,
  label,
  sub,
}: {
  big: string;
  label: string;
  sub: string;
}) {
  return (
    <div className="border-t border-forest-900/15 pt-4">
      <div className="font-display text-4xl lg:text-5xl text-forest-900 leading-none">
        {big}
      </div>
      <div className="mt-3 text-sm text-forest-900/80">{label}</div>
      <div className="text-xs uppercase tracking-widest-2 text-forest-900/50 mt-1">
        {sub}
      </div>
    </div>
  );
}

function Geography({
  imageId,
  big,
  label,
  body,
}: {
  imageId: string;
  big: string;
  label: string;
  body: string;
}) {
  return (
    <div className="group flex flex-col bg-cream border border-forest-900/10 overflow-hidden card-lift h-full">
      <div className="aspect-[4/3] overflow-hidden">
        <ImageSlot id={imageId} className="w-full h-full" aspect="auto" />
      </div>
      <div className="p-8 lg:p-10 flex-1 flex flex-col">
        <div className="font-display text-6xl text-forest-900 leading-none">
          {big}
        </div>
        <div className="mt-3 eyebrow text-clay-600">{label}</div>
        <p className="mt-5 text-forest-900/70 text-sm leading-relaxed">
          {body}
        </p>
      </div>
    </div>
  );
}

function Marker({
  x,
  y,
  label,
  sub,
}: {
  x: number;
  y: number;
  label: string;
  sub?: string;
}) {
  return (
    <g>
      <circle cx={x} cy={y} r="3" fill="#d9bc88" />
      <circle
        cx={x}
        cy={y}
        r="6"
        fill="none"
        stroke="#d9bc88"
        strokeWidth="0.5"
        opacity="0.6"
      />
      <text
        x={x + 8}
        y={y + 4}
        fill="#faf7f0"
        fontSize="10"
        fontFamily="Inter"
      >
        {label}
      </text>
      {sub && (
        <text
          x={x + 8}
          y={y + 16}
          fill="#90b193"
          fontSize="8"
          letterSpacing="1"
        >
          {sub}
        </text>
      )}
    </g>
  );
}

function BigMarker({
  x,
  y,
  label,
  sub,
}: {
  x: number;
  y: number;
  label: string;
  sub?: string;
}) {
  return (
    <g>
      <circle cx={x} cy={y} r="8" fill="#b06547" />
      <circle
        cx={x}
        cy={y}
        r="14"
        fill="none"
        stroke="#b06547"
        strokeWidth="1"
        opacity="0.6"
      />
      <circle
        cx={x}
        cy={y}
        r="20"
        fill="none"
        stroke="#b06547"
        strokeWidth="0.5"
        opacity="0.3"
      />
      <text
        x={x}
        y={y - 18}
        fill="#faf7f0"
        fontSize="14"
        fontFamily="Cormorant Garamond"
        textAnchor="middle"
        fontWeight="600"
      >
        {label}
      </text>
      {sub && (
        <text
          x={x}
          y={y + 28}
          fill="#d9bc88"
          fontSize="9"
          letterSpacing="1"
          textAnchor="middle"
        >
          {sub}
        </text>
      )}
    </g>
  );
}

function TravelTime({ time, place }: { time: string; place: string }) {
  return (
    <div>
      <div className="font-display text-3xl lg:text-4xl text-sand-300">
        {time}
      </div>
      <div className="mt-1 text-sm text-cream/80 leading-tight">{place}</div>
    </div>
  );
}

function PlaceCard({
  imageId,
  eyebrow,
  title,
  body,
  meta,
}: {
  imageId: string;
  eyebrow: string;
  title: string;
  body: string;
  meta: string;
}) {
  return (
    <div className="group flex flex-col bg-cream border border-forest-900/10 overflow-hidden card-lift h-full">
      <div className="aspect-[4/5] overflow-hidden">
        <ImageSlot id={imageId} className="w-full h-full" aspect="auto" />
      </div>
      <div className="p-8 flex-1 flex flex-col">
        <div className="eyebrow text-clay-600">{eyebrow}</div>
        <h3 className="mt-4 font-display text-2xl lg:text-3xl text-forest-900 leading-tight">
          {title}
        </h3>
        <p className="mt-4 text-forest-900/70 text-sm leading-relaxed flex-1">
          {body}
        </p>
        <div className="mt-6 text-xs uppercase tracking-widest-2 text-clay-600">
          {meta}
        </div>
      </div>
    </div>
  );
}

function Infra({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-cream p-6 lg:p-8 grid grid-cols-12 gap-4 items-baseline transition-colors hover:bg-sand-50">
      <div className="col-span-3 font-display text-xl lg:text-2xl text-forest-900">
        {title}
      </div>
      <p className="col-span-9 text-forest-900/70 leading-relaxed">{body}</p>
    </div>
  );
}
